import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { CarrierBankPanel } from "@/components/CarrierBankPanel";
import { useList } from "@/lib/hooks";
import { mediaUrl } from "@/lib/upload";
import { PageHeader, Panel, EmptyState, money } from "@/components/common";
import { InstallAppButton } from "@/components/InstallAppButton";
import { Truck, FileText, Receipt, SealCheck, FileArrowDown, Clock } from "@phosphor-icons/react";
import { openRazorpay } from "@/components/RazorpayCheckout";

const Stat = ({ label, value, testid }) => (
  <Panel className="p-5"><p className="text-xs font-bold uppercase tracking-wide text-slate-400">{label}</p><p className="font-heading font-extrabold text-2xl text-slate-900 mt-1" data-testid={testid}>{value}</p></Panel>
);

const PAY_LABEL = { processing: "Payment starting", pending: "Card payment processing", settled: "Paid", failed: "Payment failed" };
const IN_FLIGHT = ["processing", "pending"];

function InvoiceRow({ i, canPay, cardOk }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const pay = async () => {
    if (!window.confirm(`Charge ${money(i.total)} for ${i.invoice_number} to your saved card?`)) return;
    setBusy(true);
    try {
      await api.post(`/payments/pay/${i.id}`);
      toast.success("Card charge started");
      qc.invalidateQueries({ queryKey: ["carrier-portal"] });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const payCard = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/payments/pay/${i.id}/card`);
      const res = await openRazorpay(data);
      await api.post(`/payments/pay/${i.id}/verify`, res);
      toast.success("Payment received — thank you!");
      qc.invalidateQueries({ queryKey: ["carrier-portal"] });
    } catch (e) { if (e.message !== "dismissed") toast.error(e.response ? apiError(e.response.data?.detail) : e.message); }
    finally { setBusy(false); }
  };
  const open = i.status !== "paid" && !IN_FLIGHT.includes(i.payment_status);
  const showPay = canPay && open;
  return (
    <li className="py-2.5 flex flex-wrap justify-between items-center gap-2 text-sm" data-testid={`cp-invoice-${i.id}`}>
      <div><span className="font-mono text-slate-700">{i.invoice_number}</span>{i.due_date && <span className="text-xs text-slate-400 ml-2">due {i.due_date}</span>}
        {i.payment_status && <p className="text-xs text-slate-500" data-testid={`cp-invoice-paystatus-${i.id}`} title={i.payment_error || ""}>{PAY_LABEL[i.payment_status] || i.payment_status}</p>}</div>
      <span className="flex items-center gap-2 text-slate-900 font-semibold">{money(i.total)} · <span className="capitalize text-slate-500">{i.status}</span>
        {showPay && <button onClick={pay} disabled={busy} className="h-7 px-3 rounded-full bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 disabled:opacity-60" title="Charge saved card" data-testid={`cp-invoice-pay-${i.id}`}>{busy ? "…" : "Pay with Saved Card"}</button>}
        {open && cardOk && <button onClick={payCard} disabled={busy} className="h-7 px-3 rounded-full border border-slate-300 text-slate-700 text-xs font-semibold hover:bg-slate-50 disabled:opacity-60" title="Pay with any card" data-testid={`cp-invoice-paycard-${i.id}`}>Pay by Card</button>}</span>
    </li>
  );
}

export default function CarrierPortal() {
  const { data: bankInfo } = useList("pay-bank", "/payments/bank");
  const { data, isLoading } = useList("carrier-portal", "/carrier-portal/me");
  if (isLoading || !data) return <p className="text-slate-400">Loading…</p>;
  const c = data.company || {};
  const active = data.status === "Active Carrier";
  return (
    <div data-testid="carrier-portal">
      <PageHeader title={c.carrier_name || "My Company"} subtitle={`USDOT ${c.usdot || "—"} · ${c.mc_number || ""} · ${c.plan || ""}`} />
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold ${active ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`} data-testid="cp-status">
          {active ? <SealCheck size={16} weight="fill" /> : <Clock size={16} />}{data.status}
        </span>
        {data.token && <a href={`${process.env.REACT_APP_BACKEND_URL}/api/onboard/${data.token}/agreement.pdf`} className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full border border-slate-300 text-sm font-medium text-slate-700 hover:border-blue-400" data-testid="cp-agreement"><FileArrowDown size={16} />Signed Agreement</a>}
        <div className="[&_button]:h-9 [&_button]:text-sm"><InstallAppButton /></div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Stat label="Trucks" value={c.num_trucks || 0} testid="cp-trucks" />
        <Stat label="Active Loads" value={data.loads.filter((l) => (l.status || "").toLowerCase() !== "delivered").length} testid="cp-loads-count" />
        <Stat label="Open Invoices" value={data.invoices.filter((i) => i.status !== "paid").length} testid="cp-invoices-count" />
        <Stat label="Payment" value={bankInfo?.bank?.autopay ? "Card Auto Pay" : "Card"} testid="cp-payment" />
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <Panel className="p-5">
          <h3 className="font-heading font-bold text-slate-900 mb-3 flex items-center gap-2"><Truck size={18} />My Loads</h3>
          {data.loads.length === 0 ? <EmptyState icon={Truck} title="No Loads Yet" hint={active ? "Your dispatcher will book loads here." : "Loads appear once your account is authorized."} /> : (
            <ul className="divide-y divide-slate-100">{data.loads.map((l) => <li key={l.id} className="py-2.5 flex justify-between gap-3 text-sm"><span className="text-slate-800">{l.origin} → {l.destination}</span><span className="text-slate-500">{l.status}</span></li>)}</ul>
          )}
        </Panel>
        <Panel className="p-5">
          <h3 className="font-heading font-bold text-slate-900 mb-3 flex items-center gap-2"><Receipt size={18} />Invoices</h3>
          {data.invoices.length === 0 ? <EmptyState icon={Receipt} title="No Invoices Yet" hint="Weekly dispatch invoices will show here." /> : (
            <ul className="divide-y divide-slate-100">{data.invoices.map((i) => <InvoiceRow key={i.id} i={i} canPay={bankInfo?.bank?.status === "active"} cardOk={!!bankInfo?.enabled} />)}</ul>
          )}
        </Panel>
        <CarrierBankPanel />
        <Panel className="p-5 lg:col-span-2">
          <h3 className="font-heading font-bold text-slate-900 mb-3 flex items-center gap-2"><FileText size={18} />Documents</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="cp-documents">
            {data.documents.map((d) => <a key={d.id} href={mediaUrl(d.url)} target="_blank" rel="noreferrer" className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 hover:border-blue-400"><FileText size={16} className="text-blue-600" />{d.filename}</a>)}
          </div>
        </Panel>
      </div>
    </div>
  );
}
